<template>
  <layout-no-menu>
    <ion-header>
      <ion-toolbar color="primary">
        <ion-buttons slot="start">
          <ion-icon name="arrow-round-back" size="large" @click="$router.go(-1)"></ion-icon>
        </ion-buttons>
        <ion-title>Payments</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="refresh" :disabled="loading"><ion-icon name="refresh"></ion-icon></ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <!-- summary -->
      <ion-list v-if="summary">
        <ion-item v-if="summary.testMode" lines="none" color="warning">
          <ion-label text-wrap><strong>Test mode</strong><p>Payments are simulated. {{ summary.testOrderCount }} test transaction(s); totals below exclude them.</p></ion-label>
          <ion-button slot="end" size="small" fill="outline" color="dark" :disabled="busy || !summary.testOrderCount" @click="clearTest">Clear test data</ion-button>
        </ion-item>
        <ion-item lines="none">
          <ion-label text-wrap>
            <h1>{{ money(summary.netCents) }} <small style="font-weight:normal;color:gray">net collected</small></h1>
            <p>{{ money(summary.collectedCents) }} card · {{ money(summary.refundedCents) }} refunded<span v-if="summary.platformFeeCents !== null && summary.platformFeeCents !== undefined"> · {{ money(summary.platformFeeCents) }} platform fee</span></p>
            <p v-if="summary.recordedCents">{{ money(summary.recordedCents) }} cash/check recorded</p>
            <p>{{ summary.paidPlayers }} paid ({{ summary.cashPlayers }} cash) · {{ summary.unpaidPlayers }} unpaid · {{ summary.paidOrderCount }} transactions</p>
            <p v-if="summary.platformFeePercent !== null && summary.platformFeePercent !== undefined">Platform fee {{ summary.platformFeePercent }}%, {{ summary.feePayer === 'Buyer' ? 'added at checkout as a processing fee paid by the buyer' : 'deducted from your payout' }}</p>
          </ion-label>
        </ion-item>
        <ion-item v-if="!summary.accountReady" lines="none">
          <ion-label text-wrap color="warning"><p>Link a payout account under Edit Tournament → Payments first.</p></ion-label>
        </ion-item>
        <div class="ion-padding-horizontal actions">
          <ion-button expand="block" @click="newPayment = !newPayment">
            <ion-icon slot="start" name="add"></ion-icon>New payment
          </ion-button>
          <div class="actions-row">
            <ion-button fill="outline" @click="$router.push({ name: 'Pay', params: { tournament: tournamentId } })">
              <ion-icon slot="start" name="qr-scanner"></ion-icon>QR pay page
            </ion-button>
            <ion-button fill="outline" v-if="summary.provider" @click="openProvider" :disabled="busy">
              <ion-icon slot="start" name="open"></ion-icon>Open {{ summary.provider }}
            </ion-button>
          </div>
        </div>
      </ion-list>

      <!-- new payment (generated invoice) -->
      <ion-card v-if="newPayment && !generated">
        <ion-card-header><ion-card-title>New payment</ion-card-title></ion-card-header>
        <ion-card-content>
          <ion-segment :value="mode" @ionChange="mode = $event.detail.value">
            <ion-segment-button value="players"><ion-label>Players</ion-label></ion-segment-button>
            <ion-segment-button value="amount"><ion-label>Amount</ion-label></ion-segment-button>
            <ion-segment-button value="record"><ion-label>Cash / check</ion-label></ion-segment-button>
          </ion-segment>
          <template v-if="mode === 'players' || (mode === 'record' && !recordAmountMode)">
            <ion-searchbar :value="query" placeholder="Find unpaid player" debounce="300" @ionInput="search($event.target.value)" @ionChange="search($event.target.value)"></ion-searchbar>
            <ion-item v-for="p in results" :key="p.playerId">
              <ion-checkbox slot="start" :checked="selectedIds.includes(p.playerId)" :disabled="p.paid" @ionChange="toggle(p.playerId, $event)"></ion-checkbox>
              <ion-label><h3>{{ p.firstName }} {{ p.lastName }}</h3><p>{{ p.team }}</p></ion-label>
              <ion-note slot="end" :color="p.paid ? 'success' : 'medium'">{{ p.paid ? 'Paid' : money(priceCents) }}</ion-note>
            </ion-item>
            <ion-item lines="none"><ion-label>{{ selectedIds.length }} × {{ money(priceCents) }}</ion-label><ion-note slot="end" color="dark"><strong>{{ money(selectedIds.length * priceCents) }}</strong></ion-note></ion-item>
          </template>
          <template v-if="mode === 'amount' || (mode === 'record' && recordAmountMode)">
            <ion-item>
              <ion-label position="stacked">Amount (USD)</ion-label>
              <ion-input type="number" inputmode="decimal" step="0.01" min="0" :value="amount" @input="amount = $event.target.value"></ion-input>
            </ion-item>
            <ion-item>
              <ion-label position="stacked">Description</ion-label>
              <ion-input :value="description" placeholder="e.g. Team fee – Lincoln Elementary" @input="description = $event.target.value"></ion-input>
            </ion-item>
          </template>
          <template v-if="mode === 'record'">
            <ion-item lines="none">
              <ion-label>Record by</ion-label>
              <ion-segment :value="recordAmountMode ? 'amount' : 'players'" @ionChange="recordAmountMode = $event.detail.value === 'amount'" style="width:60%">
                <ion-segment-button value="players"><ion-label>Players</ion-label></ion-segment-button>
                <ion-segment-button value="amount"><ion-label>Amount</ion-label></ion-segment-button>
              </ion-segment>
            </ion-item>
            <ion-item>
              <ion-label>Method</ion-label>
              <ion-select :value="recordMethod" interface="popover" @ionChange="recordMethod = $event.detail.value">
                <ion-select-option value="Cash">Cash</ion-select-option>
                <ion-select-option value="Check">Check</ion-select-option>
              </ion-select>
            </ion-item>
            <ion-item v-if="recordMethod === 'Check'">
              <ion-label position="stacked">Check number</ion-label>
              <ion-input class="check-number" :value="recordReference" @input="recordReference = $event.target.value"></ion-input>
            </ion-item>
            <ion-item>
              <ion-label position="stacked">Note</ion-label>
              <ion-input :value="recordNote" placeholder="e.g. coach paid for the whole team" @input="recordNote = $event.target.value"></ion-input>
            </ion-item>
          </template>
          <ion-item>
            <ion-label position="stacked">Payer email (optional)</ion-label>
            <ion-input type="email" :value="payerEmail" @input="payerEmail = $event.target.value"></ion-input>
          </ion-item>
          <p style="color: red" v-if="error">{{ error }}</p>
          <ion-button v-if="mode !== 'record'" expand="block" :disabled="busy || !canGenerate || !summary.accountReady" @click="generate">{{ busy ? 'Generating…' : 'Generate payment' }}</ion-button>
          <ion-button v-else expand="block" color="success" :disabled="busy || !canGenerate" @click="record">{{ busy ? 'Saving…' : 'Record ' + recordMethod.toLowerCase() + ' payment' }}</ion-button>
          <p v-if="mode !== 'record'" style="font-size:12px;color:gray">Not saved until paid.</p>
        </ion-card-content>
      </ion-card>

      <!-- present generated payment -->
      <ion-card v-if="generated">
        <ion-card-header>
          <ion-card-subtitle>Payment #{{ generated.id }}</ion-card-subtitle>
          <ion-card-title>{{ money(generated.amountCents) }} · {{ generated.description }}</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <div class="ion-text-center" v-if="qrDataUrl">
            <img :src="qrDataUrl" alt="QR code to pay" style="width: 240px; height: 240px;" />
            <p>Scan to pay</p>
          </div>
          <ul v-if="generated.items && generated.items.length" style="padding-left:18px;font-size:14px">
            <li v-for="i in generated.items" :key="i.playerId">{{ i.description }} – {{ money(i.amountCents) }}</li>
          </ul>
          <ion-button expand="block" @click="openCheckout">Tap to pay on this device</ion-button>
          <ion-item lines="none">
            <ion-input readonly class="invoice-link" :value="invoiceLink"></ion-input>
            <ion-button slot="end" size="small" fill="outline" @click="copy(invoiceLink)">{{ copied ? 'Copied' : 'Copy link' }}</ion-button>
          </ion-item>
          <ion-button expand="block" fill="outline" color="success" :disabled="busy" @click="cash">Paid in cash</ion-button>
          <ion-button expand="block" fill="clear" color="medium" :disabled="busy" @click="discard">Discard</ion-button>
          <p style="color: red" v-if="error">{{ error }}</p>
          <p style="font-size:12px;color:gray"><ion-spinner name="dots" style="height:12px"></ion-spinner> Waiting for payment…</p>
        </ion-card-content>
      </ion-card>

      <!-- transactions -->
      <ion-list>
        <ion-list-header color="light">
          <ion-label>Transactions</ion-label>
          <download-csv v-if="csvRows.length" :data="csvRows" :name="'payments-' + tournamentId + '.csv'">
            <ion-button size="small" fill="clear">CSV</ion-button>
          </download-csv>
        </ion-list-header>
        <ion-item v-if="!loading && orders.length === 0" lines="none"><ion-label><p>No payments yet.</p></ion-label></ion-item>
        <template v-for="o in orders">
          <ion-item :key="o.id" button detail="false" @click="expanded = expanded === o.id ? null : o.id">
            <ion-label text-wrap>
              <h2>{{ money(o.amountCents) }} <ion-text :color="statusColor(o)" style="font-size:13px">{{ statusLabel(o) }}</ion-text><ion-text v-if="o.isTest" color="warning" style="font-size:12px"> TEST</ion-text></h2>
              <p>{{ playerNames(o) || o.description }}</p>
              <p>{{ methodLabel(o) }} · {{ o.buyerEmail || o.userName || 'no email' }} · {{ formatDate(o.paidDate || o.createDate) }}</p>
              <p v-if="o.note"><em>{{ o.note }}</em></p>
            </ion-label>
            <ion-icon slot="end" :name="expanded === o.id ? 'arrow-dropup' : 'arrow-dropdown'"></ion-icon>
          </ion-item>
          <ion-item v-if="expanded === o.id" :key="'d' + o.id" lines="full" color="light">
            <ion-label text-wrap>
              <p>Order #{{ o.id }} · {{ o.kind }}<span v-if="o.createdBy"> by {{ o.createdBy }}</span><span v-if="o.platformFeeCents !== null && o.platformFeeCents !== undefined"> · platform fee {{ money(o.platformFeeCents) }} ({{ o.platformFeePercent }}%)</span></p>
              <p v-if="o.externalPaymentId">Provider ref: {{ o.externalPaymentId }}</p>
              <p v-if="o.refundedCents">Refunded {{ money(o.refundedCents) }} of {{ money(o.amountCents) }}</p>
              <div style="margin-top:6px">
                <ion-button size="small" color="danger" fill="outline" v-if="isAdmin && canRefund(o)" :disabled="busy" @click.stop="refund(o)">Refund</ion-button>
                <ion-button size="small" fill="outline" v-if="o.buyerEmail" :disabled="busy" @click.stop="resend(o)">Resend receipt</ion-button>
                <ion-button size="small" fill="outline" :disabled="busy" @click.stop="editNote(o)">{{ o.note ? 'Edit note' : 'Add note' }}</ion-button>
                <ion-button size="small" fill="clear" v-for="i in playerItems(o)" :key="i.playerId" @click.stop="$router.push({ name: 'PlayerEdit', params: { tournament: tournamentId, id: i.playerId } })">{{ i.description.replace('Registration: ', '') }}</ion-button>
              </div>
            </ion-label>
          </ion-item>
        </template>
      </ion-list>
      <div style="height:120px"></div>
    </ion-content>
  </layout-no-menu>
</template>

<script>
import QRCode from "qrcode";
import PaymentAPI from "@/services/PaymentAPI";
import Authentication from "@/services/Authentication";
const paymentAPI = new PaymentAPI();
const authentication = new Authentication();

export default {
  name: "Payments",
  data() {
    const user = authentication.getUser();
    return {
      tournamentId: this.$route.params.tournament,
      user,
      summary: null,
      orders: [],
      loading: false,
      busy: false,
      error: "",
      expanded: null,
      newPayment: false,
      mode: "players",
      query: "",
      results: [],
      selectedIds: [],
      amount: "",
      description: "",
      payerEmail: "",
      recordAmountMode: false,
      recordMethod: "Cash",
      recordReference: "",
      recordNote: "",
      generated: null,
      qrDataUrl: "",
      copied: false,
      watcher: null,
    };
  },
  computed: {
    isAdmin() {
      const roles = (this.user && this.user.roles) || [];
      return roles.includes(`${this.tournamentId}-Admin`) || roles.includes("0-SuperAdmin");
    },
    priceCents() {
      return Math.round((Number(this.summary ? this.summary.price : 0) || 0) * 100);
    },
    canGenerate() {
      const byPlayers = this.mode === "players" || (this.mode === "record" && !this.recordAmountMode);
      if (byPlayers) return this.selectedIds.length > 0 && this.priceCents > 0;
      return Number(this.amount) > 0 && this.description.trim().length > 0;
    },
    invoiceLink() {
      if (!this.generated) return "";
      return `${window.location.origin}/${this.tournamentId}/Invoice/${this.generated.id}?key=${encodeURIComponent(this.generated.publicKey)}`;
    },
    csvRows() {
      return this.orders.map(o => ({
        order: o.id, status: o.status, kind: o.kind, amount: (o.amountCents / 100).toFixed(2), refunded: (o.refundedCents / 100).toFixed(2),
        ...(o.platformFeeCents !== null && o.platformFeeCents !== undefined ? { platformFee: (o.platformFeeCents / 100).toFixed(2) } : {}), method: o.paidMethod || "", reference: o.reference || "", note: o.note || "", provider: o.provider, buyer: o.buyerEmail || o.userName || "",
        players: this.playerItems(o).map(i => i.description.replace("Registration: ", "")).join("; "), description: o.description || "", test: o.isTest ? "yes" : "", feePayer: o.feePayer || "",
        paidDate: o.paidDate || "", providerRef: o.externalPaymentId || "",
      }));
    },
  },
  methods: {
    money(cents) { return PaymentAPI.money(cents); },
    formatDate(d) { const x = new Date(d); return Number.isNaN(x.getTime()) ? "" : x.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); },
    statusLabel(o) { return o.status === "PartiallyRefunded" ? "Partly refunded" : o.status; },
    playerItems(o) { return (o.items || []).filter(i => i.playerId > 0); },
    playerNames(o) { return this.playerItems(o).map(i => i.description.replace("Registration: ", "")).join(", "); },
    async clearTest() {
      try { await this.$confirm("Delete all test transactions and unmark the players they paid?"); } catch (e) { return; }
      this.busy = true;
      try { await paymentAPI.clearTestOrders(this.tournamentId); await this.refresh(); }
      catch (e) { this.error = typeof e === "string" ? e : "Could not clear test data."; }
      finally { this.busy = false; }
    },
    methodLabel(o) {
      if (o.paidMethod === "Cash") return "Cash";
      if (o.paidMethod === "Check") return "Check" + (o.reference ? " #" + o.reference : "");
      return o.provider;
    },
    async record() {
      this.busy = true;
      this.error = "";
      try {
        const body = { method: this.recordMethod, reference: this.recordReference, note: this.recordNote, payerEmail: this.payerEmail };
        if (this.recordAmountMode) { body.amountCents = Math.round(Number(this.amount) * 100); body.description = this.description.trim(); }
        else body.playerIds = this.selectedIds;
        await paymentAPI.recordPayment(this.tournamentId, body);
        this.recordReference = ""; this.recordNote = "";
        this.finishGenerated();
        this.search(this.query);
      } catch (e) {
        this.error = typeof e === "string" ? e : "Could not record the payment.";
      } finally {
        this.busy = false;
      }
    },
    async editNote(o) {
      let note;
      try { note = await this.$prompt("Note for this payment", o.note || ""); } catch (e) { return; }
      this.busy = true;
      try { await paymentAPI.updateNote(o.id, note); await this.refresh(); }
      catch (e) { this.error = typeof e === "string" ? e : "Could not save the note."; }
      finally { this.busy = false; }
    },
    statusColor(o) { return o.status === "Paid" ? "success" : o.status === "Refunded" ? "danger" : "warning"; },
    canRefund(o) { return o.status === "Paid" || o.status === "PartiallyRefunded"; },
    async refresh() {
      this.loading = true;
      this.error = "";
      try {
        [this.summary, this.orders] = await Promise.all([paymentAPI.summary(this.tournamentId), paymentAPI.tournamentOrders(this.tournamentId)]);
      } catch (e) {
        this.error = typeof e === "string" ? e : "Could not load payments.";
      } finally {
        this.loading = false;
      }
    },
    async search(value) {
      this.query = value || "";
      try { this.results = await paymentAPI.searchPlayers(this.tournamentId, this.query); } catch (e) { /* ignore */ }
    },
    toggle(id, event) {
      const checked = event && event.detail && event.detail.checked === true;
      if (checked && !this.selectedIds.includes(id)) this.selectedIds = [...this.selectedIds, id];
      else if (!checked) this.selectedIds = this.selectedIds.filter(x => x !== id);
    },
    async generate() {
      this.busy = true;
      this.error = "";
      try {
        const body = this.mode === "players"
          ? { playerIds: this.selectedIds, payerEmail: this.payerEmail }
          : { amountCents: Math.round(Number(this.amount) * 100), description: this.description.trim(), payerEmail: this.payerEmail };
        this.generated = await paymentAPI.createInvoice(this.tournamentId, body);
        this.qrDataUrl = await QRCode.toDataURL(this.generated.checkoutUrl, { width: 240, margin: 1 });
        this.startWatching();
      } catch (e) {
        this.error = typeof e === "string" ? e : "Could not generate the payment.";
      } finally {
        this.busy = false;
      }
    },
    startWatching() {
      this.stopWatching();
      this.watcher = setInterval(async () => {
        if (!this.generated) return this.stopWatching();
        try {
          const o = await paymentAPI.invoice(this.generated.id, this.generated.publicKey);
          if (o.status !== "Pending") { this.finishGenerated(); }
        } catch (e) { this.finishGenerated(); }
      }, 3000);
    },
    stopWatching() { if (this.watcher) { clearInterval(this.watcher); this.watcher = null; } },
    finishGenerated() {
      this.stopWatching();
      this.generated = null;
      this.qrDataUrl = "";
      this.newPayment = false;
      this.selectedIds = [];
      this.amount = "";
      this.description = "";
      this.refresh();
    },
    openCheckout() { if (this.generated) window.location.href = this.generated.checkoutUrl; },
    async copy(text) {
      try { await navigator.clipboard.writeText(text); this.copied = true; setTimeout(() => (this.copied = false), 2000); } catch (e) { this.error = "Copy failed."; }
    },
    async cash() {
      try { await this.$confirm(`Record ${this.money(this.generated.amountCents)} received in cash?`); } catch (e) { return; }
      this.busy = true;
      try { await paymentAPI.invoiceCash(this.generated.id); this.finishGenerated(); }
      catch (e) { this.error = typeof e === "string" ? e : "Could not record the cash payment."; }
      finally { this.busy = false; }
    },
    async discard() {
      this.busy = true;
      try { await paymentAPI.invoiceDiscard(this.generated.id); } catch (e) { /* already gone */ }
      this.busy = false;
      this.finishGenerated();
    },
    async refund(o) {
      const remaining = o.amountCents - o.refundedCents;
      let amount;
      try {
        const input = await this.$prompt(`Refund amount (max ${this.money(remaining)}). Leave as is for a full refund.`, (remaining / 100).toFixed(2));
        amount = Math.round(Number(input) * 100);
      } catch (e) { return; }
      if (!(amount > 0) || amount > remaining) { this.error = "Invalid refund amount."; return; }
      this.busy = true;
      this.error = "";
      try { await paymentAPI.refund(o.id, amount); await this.refresh(); }
      catch (e) { this.error = typeof e === "string" ? e : "Refund failed."; }
      finally { this.busy = false; }
    },
    async resend(o) {
      this.busy = true;
      try { await paymentAPI.resendReceipt(o.id); this.$alert(`Receipt sent to ${o.buyerEmail}.`); }
      catch (e) { this.error = typeof e === "string" ? e : "Could not send the receipt."; }
      finally { this.busy = false; }
    },
    async openProvider() {
      this.busy = true;
      try { const url = await paymentAPI.providerLink(this.tournamentId); window.open(url, "_blank"); }
      catch (e) { this.error = typeof e === "string" ? e : "Could not open the provider dashboard."; }
      finally { this.busy = false; }
    },
  },
  created() { this.refresh(); this.search(""); },
  beforeDestroy() { this.stopWatching(); },
};
</script>

<style scoped>
.actions-row { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.actions-row ion-button { flex: 1 1 45%; margin: 0; }
</style>
